import clsx from 'clsx';
import React from 'react';

import ClientRenderedContent from '@components/ClientRenderedContent';
import CookiesSettingsBtn from '@components/CookiesSettingsBtn';
import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import useWindowSize from '@hooks/useWindowSize';
import { Theme } from '@interfaces/global';
import { referrals_path, upgrade_subscription_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { LeadDetails } from '@utils/backend-api/users';
import { WIDTH_SM } from '@utils/constants';
import { FormLocation, LeadTypes } from '@utils/enums';
import { transformToSnakeCase } from '@utils/formatting';

import Button from '../Button';
import { PrimaryButton, ProButton } from '../Button/variants';
import EmailLeadCapture from '../EmailLeadCapture';
import { MORE_SUBMENU_SOCIAL_NAV_ITEMS } from '../Nav/navItems';
import { MORE_LINK_ITEMS } from './footerItems';

interface FormAndSocialProps {
  currentUserLoggedin: boolean;
  hideForm: boolean;
  isPro?: boolean;
  isSubscriber: boolean;
  leadDetails: LeadDetails;
  subscribePathWithPossibleOffer: string;
}

const FormAndSocial = ({
  currentUserLoggedin,
  hideForm,
  isPro,
  isSubscriber,
  leadDetails,
  subscribePathWithPossibleOffer,
}: FormAndSocialProps) => {
  const hydrated = useServerSideRendering();
  const { width: windowWidth } = useWindowSize();
  const { trackEvent } = useEventTracking();

  const getFormHeader = () => {
    if (!isSubscriber && !currentUserLoggedin && hideForm) {
      return null;
    } else if (!isSubscriber) {
      return 'Subscribe to The Information';
    } else if (isSubscriber && !isPro) {
      return 'Become a Pro subscriber';
    } else {
      return 'Share The Information';
    }
  };

  const getFormAction = () => {
    if (!isSubscriber && !currentUserLoggedin && hideForm) {
      return null;
    } else if (!isSubscriber && !currentUserLoggedin) {
      return (
        <div>
          <EmailLeadCapture
            buttonText="Get started"
            formLocation={FormLocation.Footer}
            redirectToOnSuccess={subscribePathWithPossibleOffer}
            leadType={LeadTypes.Footer}
            theme={Theme.Dark}
            isVertical={windowWidth < WIDTH_SM}
            buttonClassName="w-full sm:w-[120px]"
            leadDetails={leadDetails}
            isPro={isPro}
            inputClassName="w-full placeholder:text-grayscale-beige  placeholder:opacity-50"
            includeSignInOption
            gdprClassName="mb-8 sm:mb-16"
          />
        </div>
      );
    } else if (!isSubscriber && currentUserLoggedin) {
      return (
        <PrimaryButton
          as="link"
          href={subscribePathWithPossibleOffer}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.GetStarted }))}
        >
          Get started
        </PrimaryButton>
      );
    } else if (!isPro && isSubscriber) {
      return (
        <ProButton
          as="link"
          href={upgrade_subscription_path()}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Upgrade }))}
        >
          Upgrade
        </ProButton>
      );
    } else {
      return (
        <PrimaryButton
          as="link"
          href={referrals_path()}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.ReferAFriend }))}
        >
          Refer a friend
        </PrimaryButton>
      );
    }
  };

  return (
    <div
      className={clsx('flex w-full flex-col pr-0 pt-72 sm:w-auto sm:pt-0 md:pr-112', {
        'justify-end': !isSubscriber && !currentUserLoggedin && hideForm,
      })}
    >
      <div>
        <div className="mb-24 font-suisse-works text-subheadline-sm leading-[164%] text-white sm:text-headline-xs">
          {getFormHeader()}
        </div>
        {hydrated && getFormAction()}
      </div>
      <div
        className={clsx('mt-48', {
          '!mt-0': !isSubscriber && !currentUserLoggedin && hideForm,
        })}
      >
        <div className="mb-32 flex w-full justify-around sm:w-[180px] sm:justify-between">
          {MORE_SUBMENU_SOCIAL_NAV_ITEMS.map(({ icon: Icon, name, ...rest }) => (
            <Button
              className="flex h-[21px] w-[21px] items-center justify-center bg-transparent text-white transition-colors hover:border-primary-magenta hover:text-primary-magenta focus:border-primary-magenta focus:text-primary-magenta"
              as="externalLink"
              key={name}
              onClickCapture={() => trackEvent(click({ clickTarget: transformToSnakeCase(name) }))}
              {...rest}
            >
              <Icon className="h-[21px]" />
              <span className="sr-only">{name}</span>
            </Button>
          ))}
        </div>
        <div className="mb-8 flex justify-between">
          {MORE_LINK_ITEMS.map(({ name, href, ...rest }) => (
            <Button
              className="mt-8 !w-auto !justify-start font-suisse-screen text-overline-md text-grayscale-beige no-underline opacity-50 transition-colors hover:text-primary-magenta hover:opacity-100 focus:text-primary-magenta"
              as="link"
              key={href}
              href={href}
              onClickCapture={() => trackEvent(click({ clickTarget: transformToSnakeCase(name) }))}
              {...rest}
            >
              {name}
            </Button>
          ))}
        </div>
        <div className="mt-8 font-suisse-screen text-overline-md text-grayscale-beige opacity-50">
          &#169; 2013&ndash;
          <ClientRenderedContent>{new Date().getFullYear()}</ClientRenderedContent> The Information.
          All Rights Reserved.
        </div>

        <CookiesSettingsBtn />
      </div>
    </div>
  );
};

export default FormAndSocial;
